import { useState, useId } from "react";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as UserIcon } from "../../../assets/user.icon.svg";
import { UserRole } from "../../../common/enums/user-role.enum";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import { DropdownMenuType, SelectionFilter } from "../../menu-filter/menu-filter.comp";
import { KBMultiSelect, KBOption } from "./kb-multi-select.comp";

export const BulkChangeRoleModal = ({
    open,
    knowledgeBases,
    handleClose,
    handleAction
}: {
    open: boolean;
    knowledgeBases: KBOption[];
    handleClose: () => void;
    handleAction: (kbs: KBOption[], role: string) => void;
}) => {
    const [selectedRole, setSelectedRole] = useState<UserRole>();
    const [selectedKBs, setSelectedKBs] = useState<KBOption[]>([]);
    // Base id for associating the visible "New Role" <label> with its control (WCAG 1.3.1).
    const fieldId = useId();

    const handleCloseClick = () => {
        handleClose();
        setSelectedRole(undefined);
        setSelectedKBs([]);
    };

    const handleActionClick = () => {
        if (selectedRole && selectedKBs.length > 0) {
            handleAction(selectedKBs, selectedRole);
            setSelectedRole(undefined);
            setSelectedKBs([]);
        }
    };

    return (
        <ModalTemplate
            open={open}
            width={654}
            handleClose={handleCloseClick}
            ariaLabel="Change Role"
        >
            <div className="um-add-users-modal">
                <div className="um-add-users-modal-title">
                    <SvgIcon
                        className="um-add-users-modal-title__icon"
                        component={UserIcon}
                        inheritViewBox
                    />
                    <span className="um-add-users-modal-title__text">Change Role</span>
                </div>
                <div className="um-add-users-modal-description">
                    <span className="um-add-users-modal-description__text">
                        Select the assistants and new role for the selected user(s).
                    </span>
                </div>
                <div className="um-add-users-modal-field">
                    {/* Visual caption only; KBMultiSelect names itself via aria-label. */}
                    <span className="um-add-users-modal-field__label">Assistants</span>
                    <KBMultiSelect
                        knowledgeBases={knowledgeBases}
                        selectedKBs={selectedKBs}
                        setSelectedKBs={setSelectedKBs}
                    />
                </div>
                <div className="um-add-users-modal-field">
                    {/* Associated with the control below via SelectionFilter's aria-labelledby. */}
                    <span className="um-add-users-modal-field__label" id={`${fieldId}-role-label`}>
                        New Role
                    </span>
                    <SelectionFilter
                        ariaLabelledBy={`${fieldId}-role-label`}
                        list={[UserRole.user, UserRole.manager].map(
                            role => role.charAt(0).toUpperCase() + role.slice(1)
                        )}
                        selectedItem={
                            selectedRole
                                ? selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)
                                : undefined
                        }
                        setSelectedItem={(role: string) => {
                            setSelectedRole(role.toLowerCase() as UserRole);
                        }}
                        placeholder="Select New Role"
                        dropdownMenuType={DropdownMenuType.wider}
                        rightPosition={true}
                        minWidth={320}
                    />
                </div>
                <div className="um-add-users-modal-action">
                    <Button
                        className="primary-button"
                        variant="text"
                        onClick={handleActionClick}
                        disabled={!selectedRole || selectedKBs.length === 0}
                    >
                        Change Role
                    </Button>
                </div>
            </div>
        </ModalTemplate>
    );
};
